if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Quick View injects a copy of product-info; never bind that copy to the PDP bar.
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }

        this.button = this.querySelector('[data-sticky-atc-button]');
        this.label = this.querySelector('[data-sticky-atc-label]');
        this.selectedEl = this.querySelector('[data-sticky-atc-selected]');
        this.imageEl = this.querySelector('[data-sticky-atc-image]');
        this.titleEl = this.querySelector('.sticky-atc__title');
        this.sectionId = this.dataset.section;
        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        if (this.productInfo?.closest('quick-add-modal')) {
          this.productInfo = null;
        }
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.onMainClick = this.onMainClick.bind(this);
        this.onPrimaryAtcInteract = this.onPrimaryAtcInteract.bind(this);
        this.syncFromDom = this.syncFromDom.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onOptionValueChange = this.onOptionValueChange.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);

        this.hasAtcInteracted = false;
        this.mainButtonPassed = false;

        this.button?.addEventListener('click', this.onMainClick);
        this.mainButton?.addEventListener('click', this.onPrimaryAtcInteract);
        this.bindObserver();
        this.bindVariantEvents();
        this.syncFromDom();

        // Ensure bar is in body for fixed positioning
        if (!this.dataset.moved) {
          this.dataset.moved = 'true';
          document.body.appendChild(this);
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onMainClick);
        this.mainButton?.removeEventListener('click', this.onPrimaryAtcInteract);
        this.observer?.disconnect();
        this.footerObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.optionUnsubscriber?.();
        document.removeEventListener('change', this.onQuantityChange);
        if (this.classList.contains('is-visible')) {
          document.body.classList.remove('sticky-atc-visible');
        }
      }

      onPrimaryAtcInteract() {
        if (this.hasAtcInteracted) return;
        this.hasAtcInteracted = true;
        this.updateVisibility();
      }

      bindObserver() {
        this.mainButtonPassed = false;
        this.mainButtonInView = Boolean(this.mainButton);
        this.footerNear = false;

        if (typeof IntersectionObserver === 'undefined') {
          this.updateVisibility();
          return;
        }

        if (this.mainButton) {
          this.observer = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry) return;
              // Show sticky only after the primary ATC has scrolled above the viewport —
              // not while it is still below the fold / not yet reached.
              this.mainButtonInView = entry.isIntersecting;
              this.mainButtonPassed = entry.boundingClientRect.bottom < 0;
              this.updateVisibility();
            },
            { root: null, threshold: 0, rootMargin: '0px' }
          );
          this.observer.observe(this.mainButton);
        }

        const footer =
          document.querySelector('footer.footer') ||
          document.querySelector('footer') ||
          document.querySelector('.shopify-section-group-footer-group');

        if (footer) {
          // Expand root below the viewport so we hide before the bar overlaps footer content.
          const buffer = Math.max(this.offsetHeight || 0, 96) + 32;
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry) return;
              this.footerNear = entry.isIntersecting;
              this.updateVisibility();
            },
            { root: null, threshold: 0, rootMargin: `0px 0px ${buffer}px 0px` }
          );
          this.footerObserver.observe(footer);
        }

        this.updateVisibility();
      }

      updateVisibility() {
        const unlocked = this.mainButtonPassed || this.hasAtcInteracted;
        const shouldShow = unlocked && !this.mainButtonInView && !this.footerNear;
        if (shouldShow) this.show();
        else this.hide();
      }

      bindVariantEvents() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
          this.optionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onOptionValueChange);
        }

        // Fallback: watch product form button text mutations
        if (this.mainButton && typeof MutationObserver !== 'undefined') {
          this.buttonObserver = new MutationObserver(() => this.syncFromDom());
          this.buttonObserver.observe(this.mainButton, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['disabled', 'data-preorder', 'aria-disabled'],
          });
        }

        document.addEventListener('change', this.onQuantityChange);
      }

      isOwnProductEvent(event) {
        const sectionId = event?.data?.sectionId;
        if (sectionId) return sectionId === this.sectionId;

        const eventTarget = event?.data?.event?.target || event?.data?.target;
        if (eventTarget && this.productInfo) {
          return this.productInfo.contains(eventTarget);
        }

        return false;
      }

      onVariantChange(event) {
        if (!this.isOwnProductEvent(event)) return;
        this.syncFromDom();
      }

      onOptionValueChange(event) {
        if (!this.isOwnProductEvent(event)) return;
        requestAnimationFrame(this.syncFromDom);
      }

      onQuantityChange(event) {
        if (!this.productInfo?.contains(event.target)) return;
        if (event.target.matches?.('.quantity__input')) {
          this.syncFromDom();
        }
      }

      show() {
        if (this.classList.contains('is-visible') && !this.hasAttribute('hidden')) return;

        // Drop [hidden] so CSS can animate; paint once off-screen, then slide in
        this.hidden = false;
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('sticky-atc-visible');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.classList.add('is-visible');
          });
        });
      }

      hide() {
        if (!this.classList.contains('is-visible') && this.hasAttribute('hidden')) return;

        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sticky-atc-visible');
        // Keep in DOM (no [hidden]) so the slide-out transition can finish
      }

      onMainClick() {
        if (this.button?.hasAttribute('disabled')) return;

        const form = this.productInfo?.querySelector('product-form');
        const mainSubmit = this.mainButton || form?.querySelector('[type="submit"]');

        if (mainSubmit) {
          mainSubmit.click();
          return;
        }

        form?.querySelector('form')?.requestSubmit?.();
      }

      formatMoney(cents, format) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, format);
        }
        const value = (Number(cents) / 100).toFixed(2);
        return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, value);
      }

      getSelectedValues() {
        const selects = this.productInfo?.querySelector('variant-selects');
        if (!selects) return [];

        const values = [];
        selects.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          if (checked?.value) values.push(checked.value);
        });
        selects.querySelectorAll('select.select__select, .product-form__input--dropdown select').forEach((select) => {
          const value = select.selectedOptions?.[0]?.value;
          if (value) values.push(value);
        });
        return values;
      }

      syncFromDom() {
        if (!this.productInfo) return;

        const form = this.productInfo.querySelector('product-form');
        const mainSubmit = this.mainButton || form?.querySelector('.product-form__submit');
        const mainLabel = mainSubmit?.querySelector('span')?.textContent?.trim();
        const isPreorder = mainSubmit?.getAttribute('data-preorder') === 'true';
        const isDisabled = mainSubmit?.hasAttribute('disabled') && !isPreorder;

        if (this.titleEl && this.dataset.productTitle) {
          this.titleEl.textContent = this.dataset.productTitle;
        }

        if (this.label && mainLabel) {
          this.label.textContent = mainLabel;
        }

        if (this.button) {
          this.button.toggleAttribute('disabled', Boolean(isDisabled));
          this.button.classList.toggle('sticky-atc__button--preorder', isPreorder);
          if (isPreorder) this.button.setAttribute('data-preorder', 'true');
          else this.button.removeAttribute('data-preorder');
        }

        const selected = this.getSelectedValues();
        if (this.selectedEl) {
          this.selectedEl.textContent = selected.join(', ');
        }

        // Prefer active gallery image / variant featured media thumb
        const activeImg =
          this.productInfo.querySelector(
            '.product__media-item.is-active img, .product__media-list .product__media-item.is-active img'
          ) || this.productInfo.querySelector('.product__media img');

        if (activeImg && this.imageEl?.tagName === 'IMG') {
          const src = activeImg.currentSrc || activeImg.src;
          if (src) {
            this.imageEl.src = src;
            this.imageEl.srcset = '';
          }
        }
      }
    }
  );
}
